import React, { useRef } from "react";

import type { MediaItem } from "../../media-widget/types/contexts";
import { usePlaylist } from "../../media-widget/hooks/usePlaylistContext";
import useStoryTimer from "../../media-widget/hooks/useStoryTimer";
import { useSwipeNavigation } from "../../media-widget/hooks/useSwipeNavigation";
import { useIsMobile } from "../../utils/useMediaQuery";
import MediaPlayer from "../media/MediaPlayer";
import MediaBackground from "./MediaBackground";
import MediaControls from "./MediaControls";
import ProgressBar from "./ProgressBar";
import ThumbnailNavigation from "./ThumbnailNavigation";

interface PlaylistContentProps {
  isPlaylist?: boolean;
}

const PlaylistContent: React.FC<PlaylistContentProps> = ({
  isPlaylist = true,
}) => {
  const { mediaItems, currentMediaIndex, setCurrentMediaIndex, isPlaying } =
    usePlaylist();

  const { handleTouchStart, handleTouchEnd } = useSwipeNavigation();
  const isMobile = useIsMobile();
  const videoRef = useRef<HTMLVideoElement>(
    null,
  ) as React.RefObject<HTMLVideoElement>;

  const handleNextStory = () => {
    if (currentMediaIndex < mediaItems.length - 1) {
      setCurrentMediaIndex(currentMediaIndex + 1);
    }
  };

  const { progress } = useStoryTimer({
    currentIndex: currentMediaIndex,
    duration: 15,
    videoRef:
      mediaItems[currentMediaIndex] &&
      mediaItems[currentMediaIndex].type === "video"
        ? videoRef
        : undefined,
    onComplete: handleNextStory,
    isPlaying,
  });

  const currentMedia = mediaItems[currentMediaIndex];
  if (!currentMedia) return null;

  return (
    <div className="relative flex h-full w-full">
      {/* Background always rendered, absolutely positioned */}
      <div className="absolute inset-0 z-0">
        <MediaBackground media={currentMedia} />
      </div>
      <div className="relative z-20 flex h-full w-full">
        {/* Foreground content */}
        {isPlaylist && !isMobile && <ThumbnailNavigation />}
        <div
          className="relative flex w-full min-w-0 flex-col items-center justify-center overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {isPlaylist && isMobile && <ProgressBar progress={progress} />}
          <div className="z-20 flex items-center justify-center gap-4 px-8">
            <MediaContent currentMedia={currentMedia} />
          </div>
        </div>
      </div>
    </div>
  );
};

const MediaContent = ({ currentMedia }: { currentMedia: MediaItem }) => {
  const videoRef = useRef<HTMLVideoElement>(
    null,
  ) as React.RefObject<HTMLVideoElement>;
  const { isMuted, setIsPlaying } = usePlaylist();
  return (
    <div className="relative z-20 flex items-center justify-center gap-0 px-8 md:gap-4">
      <MediaPlayer
        media={currentMedia}
        isMuted={isMuted}
        videoRef={videoRef}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      <div className="z-20">
        {/* Media Controls */}
        <MediaControls />
      </div>
    </div>
  );
};

export default PlaylistContent;
